/**
 * Navigation Utilities
 *
 * Functions for managing navigation through questionnaire categories and questions
 */

import type {
  QuestionnaireCategory,
  QuestionnaireLayer,
  QuestionnaireQuestion,
} from "../questionnaire.service";

export interface CategoryGroup {
  layer: QuestionnaireLayer;
  categories: QuestionnaireCategory[];
  questionCount: number;
}

/**
 * Group categories by layer for organized navigation
 */
export function groupCategoriesByLayer(
  layers: QuestionnaireLayer[],
  categories: QuestionnaireCategory[],
  questions: QuestionnaireQuestion[],
): CategoryGroup[] {
  return layers
    .sort((a, b) => a.display_order - b.display_order)
    .map((layer) => {
      const layerCategories = categories
        .filter((c) => c.layer_id === layer.id)
        .sort((a, b) => a.display_order - b.display_order);

      const questionCount = layerCategories.reduce((count, category) => {
        return (
          count + questions.filter((q) => q.category_id === category.id).length
        );
      }, 0);

      return {
        layer,
        categories: layerCategories,
        questionCount,
      };
    });
}

/**
 * Get questions for a specific category, sorted by display order
 */
export function getQuestionsForCategory(
  categoryId: string,
  questions: QuestionnaireQuestion[],
): QuestionnaireQuestion[] {
  return questions
    .filter((q) => q.category_id === categoryId)
    .sort((a, b) => a.display_order - b.display_order);
}

/**
 * Find the next category in sequence
 */
export function getNextCategory(
  currentCategoryId: string,
  categories: QuestionnaireCategory[],
): QuestionnaireCategory | null {
  const sortedCategories = [...categories].sort(
    (a, b) => a.display_order - b.display_order,
  );
  const currentIndex = sortedCategories.findIndex(
    (c) => c.id === currentCategoryId,
  );

  if (currentIndex === -1 || currentIndex === sortedCategories.length - 1) {
    return null;
  }

  return sortedCategories[currentIndex + 1];
}

/**
 * Find the previous category in sequence
 */
export function getPreviousCategory(
  currentCategoryId: string,
  categories: QuestionnaireCategory[],
): QuestionnaireCategory | null {
  const sortedCategories = [...categories].sort(
    (a, b) => a.display_order - b.display_order,
  );
  const currentIndex = sortedCategories.findIndex(
    (c) => c.id === currentCategoryId,
  );

  if (currentIndex <= 0) {
    return null;
  }

  return sortedCategories[currentIndex - 1];
}

/**
 * Check if a category is the first in the questionnaire
 */
export function isFirstCategory(
  categoryId: string,
  categories: QuestionnaireCategory[],
): boolean {
  const sortedCategories = [...categories].sort(
    (a, b) => a.display_order - b.display_order,
  );
  return sortedCategories[0]?.id === categoryId;
}

/**
 * Check if a category is the last in the questionnaire
 */
export function isLastCategory(
  categoryId: string,
  categories: QuestionnaireCategory[],
): boolean {
  const sortedCategories = [...categories].sort(
    (a, b) => a.display_order - b.display_order,
  );
  return sortedCategories[sortedCategories.length - 1]?.id === categoryId;
}

/**
 * Get the first category in the questionnaire
 */
export function getFirstCategory(
  categories: QuestionnaireCategory[],
): QuestionnaireCategory | null {
  const sortedCategories = [...categories].sort(
    (a, b) => a.display_order - b.display_order,
  );
  return sortedCategories[0] || null;
}

/**
 * Calculate progress through categories
 */
export function calculateCategoryProgress(
  currentCategoryId: string,
  categories: QuestionnaireCategory[],
): {
  currentIndex: number;
  totalCategories: number;
  percentage: number;
} {
  const sortedCategories = [...categories].sort(
    (a, b) => a.display_order - b.display_order,
  );
  const currentIndex = sortedCategories.findIndex(
    (c) => c.id === currentCategoryId,
  );

  return {
    currentIndex: currentIndex + 1, // 1-indexed for display
    totalCategories: sortedCategories.length,
    percentage: Math.round(
      ((currentIndex + 1) / sortedCategories.length) * 100,
    ),
  };
}

/**
 * Get category by ID
 */
export function getCategoryById(
  categoryId: string,
  categories: QuestionnaireCategory[],
): QuestionnaireCategory | undefined {
  return categories.find((c) => c.id === categoryId);
}

/**
 * Get layer for a category
 */
export function getLayerForCategory(
  categoryId: string,
  categories: QuestionnaireCategory[],
  layers: QuestionnaireLayer[],
): QuestionnaireLayer | undefined {
  const category = getCategoryById(categoryId, categories);
  if (!category) return undefined;

  return layers.find((l) => l.id === category.layer_id);
}
